import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propertiesAPI, Property, getImageUrl } from '../api/api';
import { useLanguage } from '../contexts/LanguageContext';

const PropertyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imageError, setImageError] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (id) {
      loadProperty(Number(id));
    }
  }, [id]);

  const loadProperty = async (propertyId: number) => {
    try {
      setLoading(true);
      const response = await propertiesAPI.get(propertyId);
      setProperty(response.data);
      setImageError(false); // Reset image error when loading new property
      setCurrentImageIndex(0); // Reset to first image
    } catch (error) {
      console.error('Failed to load property', error);
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPhone = (phone?: string) => {
    if (!phone) return;
    navigator.clipboard.writeText(phone);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  const images = property.images && property.images.length > 0
    ? property.images
        .filter(img => img.image_url) // Filter out empty image URLs
        .map(img => ({ ...img, fullUrl: getImageUrl(img.image_url) }))
    : [];

  const currentImage = images[currentImageIndex]?.fullUrl || '';
  const ownerName = property.owner?.first_name
    ? `${property.owner.first_name} ${property.owner.last_name || ''}`.trim()
    : property.owner?.username
    ? `@${property.owner.username}`
    : language === 'ru' ? 'Продавец' : 'Sotuvchi';

  const ownerPhone = property.owner?.phone || '+998 90 123 45 67';
  const ownerTelegram = property.owner?.username ? `https://t.me/${property.owner.username}` : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 md:pb-12">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 sm:mb-6 text-gray-600 hover:text-primary-600 flex items-center text-sm font-semibold transition"
      >
        <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
        {t('back')}
      </button>

      <div className="grid lg:grid-cols-2 gap-6 sm:gap-10">
        {/* Gallery Column */}
        <div>
          <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden mb-3 shadow-lg">
            {currentImage && !imageError ? (
              <img
                src={currentImage}
                alt={property.title}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
                loading="eager"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gradient-to-br from-primary-50 to-accent-50">
                <span className="text-7xl mb-3">🏠</span>
                <span className="text-sm font-semibold text-gray-500">{t('noListingsYet')}</span>
              </div>
            )}

            {/* Navigation arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => {
                    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
                    setImageError(false);
                  }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-xl backdrop-blur-sm transition active:scale-95"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
                    setImageError(false);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-xl backdrop-blur-sm transition active:scale-95"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-primary-200 scrollbar-track-gray-100">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentImageIndex(index);
                    setImageError(false);
                  }}
                  className={`w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-xl overflow-hidden border-3 transition-all ${
                    currentImageIndex === index
                      ? 'border-primary-600 scale-95 shadow-lg ring-2 ring-primary-200'
                      : 'border-gray-200 opacity-60 hover:opacity-100 hover:border-primary-300'
                  }`}
                >
                  <img
                    src={image.fullUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details Column */}
        <div className="flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between gap-2 mb-3">
              <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-xs sm:text-sm font-bold">
                {property.property_type === 'apartment' ? t('apartment') : t('house')}
              </span>
              {property.status === 'sold' && (
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-bold">
                  {t('sold')}
                </span>
              )}
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 leading-tight">
              {property.title}
            </h1>

            <div className="text-3xl sm:text-4xl font-black text-primary-600 mb-4">
              {Number(property.price).toLocaleString()} <span className="text-xl font-bold text-gray-500">{t('sum')}</span>
            </div>

            <div className="flex items-center text-gray-600 mb-6 text-sm sm:text-base">
              <svg className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span>{property.address ? `${property.address}, ` : ''}{property.city}</span>
            </div>

            {/* Characteristic chips */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-100">
                <div className="text-gray-500 text-xs mb-1">{t('area')}</div>
                <div className="text-lg font-bold text-gray-900">{property.area} {t('sqm')}</div>
              </div>
              {property.rooms && (
                <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-100">
                  <div className="text-gray-500 text-xs mb-1">{t('rooms')}</div>
                  <div className="text-lg font-bold text-gray-900">{property.rooms} {t('roomsShort')}</div>
                </div>
              )}
              {property.bathrooms && (
                <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-100">
                  <div className="text-gray-500 text-xs mb-1">{t('bathrooms')}</div>
                  <div className="text-lg font-bold text-gray-900">{property.bathrooms}</div>
                </div>
              )}
              {property.floor && (
                <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-100">
                  <div className="text-gray-500 text-xs mb-1">{t('floor')}</div>
                  <div className="text-lg font-bold text-gray-900">
                    {property.floor}{property.total_floors ? `/${property.total_floors}` : ''}
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="card p-5 mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2">{t('description')}</h2>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                {property.description || '—'}
              </p>
            </div>
          </div>

          {/* Contact Seller Button (Desktop) */}
          <div className="hidden md:block">
            <button
              onClick={() => setShowContactModal(true)}
              className="btn btn-primary w-full py-4 text-base font-bold shadow-lg hover:shadow-xl transition-all"
            >
              📞 {t('contactSeller')}
            </button>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Bar for Mobile */}
      <div className="md:hidden fixed bottom-14 left-0 right-0 p-3 bg-white/95 backdrop-blur-md border-t shadow-2xl z-40">
        <div className="flex items-center justify-between gap-3 max-w-lg mx-auto">
          <div>
            <div className="text-xs text-gray-500">{t('price')}</div>
            <div className="text-lg font-extrabold text-primary-600">
              {Number(property.price).toLocaleString()} {t('sum')}
            </div>
          </div>
          <button
            onClick={() => setShowContactModal(true)}
            className="btn btn-primary py-2.5 px-5 text-sm font-bold shadow"
          >
            📞 {t('contactSeller')}
          </button>
        </div>
      </div>

      {/* Contact Seller Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 z-50 animate-fadeIn">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 shadow-2xl animate-slideUp">
            <div className="flex justify-between items-center mb-5 pb-3 border-b">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                <span>👤</span> {t('sellerInfo')}
              </h3>
              <button
                onClick={() => setShowContactModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-900 font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Seller Name */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">{t('sellerName')}</p>
                <p className="text-base font-bold text-gray-900">{ownerName}</p>
              </div>

              {/* Phone */}
              <div className="bg-primary-50 p-4 rounded-xl border border-primary-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-primary-600 mb-0.5">{t('sellerPhone')}</p>
                  <p className="text-lg font-mono font-extrabold text-primary-900">{ownerPhone}</p>
                </div>
                <button
                  onClick={() => handleCopyPhone(ownerPhone)}
                  className="text-xs font-semibold px-3 py-1.5 bg-white text-primary-700 rounded-lg shadow-sm border border-primary-200 active:scale-95 transition"
                >
                  {copied ? '✓ Скопировано' : '📋 Копировать'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href={`tel:${ownerPhone.replace(/\s+/g, '')}`}
                className="btn bg-green-600 hover:bg-green-700 text-white font-bold py-3 text-center rounded-xl flex items-center justify-center gap-2 shadow"
              >
                <span>📞</span> {t('callPhone')}
              </a>

              {ownerTelegram ? (
                <a
                  href={ownerTelegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 text-center rounded-xl flex items-center justify-center gap-2 shadow"
                >
                  <span>✈️</span> Telegram
                </a>
              ) : (
                <button
                  onClick={() => setShowContactModal(false)}
                  className="btn btn-secondary py-3 text-center rounded-xl font-bold"
                >
                  {t('close')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetailPage;
